// Session transport: the four calls that carry the httpOnly refresh cookie, and the single-flight
// refresh every other request funnels through (task 326.18; findings WEB-001 / WEB-003 / X-005,
// decision Q7).
//
// Two things changed here and they are the point of the task:
//
// 1. ONE ORIGIN. Auth used to go to the core API (VITE_AUTH_API_URL) while everything else went to
//    the console BFF. A cookie set by one host is not sent to the other, which is why the refresh
//    token lived in localStorage — a 7-day rotating credential for an operator with GL and Admin
//    rights, readable by any script on the page. Auth now lives on the BFF, the token is an
//    httpOnly cookie, and the SPA holds only the 15-minute access token, in memory.
//
// 2. SINGLE FLIGHT. The server rotates the refresh token on every use, so two concurrent refreshes
//    cannot both win. The old client ran one refresh per 401 (a dashboard mounts three to five
//    queries at once) and, separately, threw the rotated token away on the proactive path — which is
//    why sessions died roughly 25 minutes in, and why any reload after 12 minutes logged the
//    operator out. Every refresh in the app now awaits the same promise.

import ky from 'ky';
import type { ConsoleSession } from './contracts/session';

const API_BASE = import.meta.env.VITE_API_BASE_URL as string;

/**
 * ky instance for the endpoints that set or read the refresh cookie. `credentials: 'include'` is
 * mandatory in both directions for a cross-site cookie: without it the browser neither stores the
 * Set-Cookie from login nor sends it back on refresh, and the session would silently never survive
 * a reload. It is why the BFF's CORS allows credentials for an explicit origin list.
 */
const sessionClient = ky.create({
  prefixUrl: API_BASE,
  credentials: 'include',
  timeout: 30_000,
  retry: 0,
});

export const sessionApi = {
  /**
   * `bearer` is only needed by invitation acceptance, which is an authenticated call that also
   * issues a new session. The token is passed in rather than read from the store so this module
   * stays free of a dependency on it (the store imports these endpoints).
   */
  post: <T>(url: string, json?: unknown, bearer?: string | null): Promise<T> =>
    sessionClient
      .post(url, {
        ...(json === undefined ? {} : { json }),
        ...(bearer ? { headers: { Authorization: `Bearer ${bearer}` } } : {}),
      })
      .json<T>(),

  postVoid: (url: string): Promise<void> => sessionClient.post(url).then(() => undefined),

  /** A POST with a body whose success response is empty (204/202) — nothing to parse. */
  postNoContent: (url: string, json: unknown, bearer?: string | null): Promise<void> =>
    sessionClient
      .post(url, {
        json,
        ...(bearer ? { headers: { Authorization: `Bearer ${bearer}` } } : {}),
      })
      .then(() => undefined),
};

// ---------------------------------------------------------------------------
// Single-flight refresh
// ---------------------------------------------------------------------------

let inFlight: Promise<ConsoleSession> | null = null;

/**
 * Exchanges the refresh cookie for a new access token. Concurrent callers share one request and one
 * outcome: with rotate-on-use, a second parallel refresh would present a token the first has already
 * spent.
 *
 * Rejects when there is no usable session. Callers must treat that as "log in again", never as
 * something to retry.
 */
export function refreshSession(): Promise<ConsoleSession> {
  if (inFlight) return inFlight;

  const request = sessionClient
    .post('api/console/auth/refresh', { timeout: 10_000 })
    .json<ConsoleSession>();

  inFlight = request;

  // Cleared on BOTH outcomes, and with a handler on each side: a rejected refresh that only had a
  // .finally() attached surfaces as an unhandled rejection even though every caller handles it.
  const clear = (): void => {
    if (inFlight === request) inFlight = null;
  };
  request.then(clear, clear);

  return request;
}

/** Test seam: forget any in-flight refresh between cases. */
export function resetRefreshStateForTests(): void {
  inFlight = null;
}

// ---------------------------------------------------------------------------
// Ending a session visibly
// ---------------------------------------------------------------------------

let ending = false;

/**
 * Clears client state and sends the operator to a login screen that says what happened.
 *
 * <p>Deliberately a full navigation and deliberately guarded: several failing requests must produce
 * ONE redirect, not a loop, and the operator must land on a login form rather than a spinner or a
 * blank page. "Sessions must not die silently" cuts both ways — when one does end, say so.</p>
 */
export function endSession(reason: 'expired' | 'signed-out' = 'expired'): void {
  if (ending) return;
  ending = true;

  const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
  const query = reason === 'expired' ? `?reason=expired&returnTo=${returnTo}` : '';
  window.location.href = `/login${query}`;
}
